export const Utils = {
    /*
    ------------------------------------------------------------------------
    Get the previous step in the step sequence.

    @param {Object} store - The Pinia store instance.
    @param {string} stepName - The current step name.
    @return {string} The previous step name, or first step if not found.
    ------------------------------------------------------------------------
    */
    getPrevStepName(store, stepName) {
        const currentIndex = store.stepNames.indexOf(stepName);
        if (currentIndex === -1 || currentIndex === 0) {
            return store.stepNames[0];
        }
        return store.stepNames[currentIndex - 1];
    },
    /*
    ------------------------------------------------------------------------
    Get the next step in the step sequence.

    @param {Object} store - The Pinia store instance.
    @param {string} stepName - The current step name.
    @return {string} The next step name, or last step if not found.
    ------------------------------------------------------------------------
    */
    getNextStepName(store, stepName) {
        const currentIndex = store.stepNames.indexOf(stepName);
        if (currentIndex === -1 || currentIndex === store.stepNames.length - 1) {
            return store.stepNames[store.stepNames.length - 1];
        }
        return store.stepNames[currentIndex + 1];
    },
    /*
    ------------------------------------------------------------------------
    Debounce function execution.

    @param {Function} func - The function to debounce.
    @param {number} wait - The delay in milliseconds.
    @return {Function} The debounced function.
    ------------------------------------------------------------------------
    */
    debounce(func, wait) {
        let timeout;
        return function (...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    },
    /*
    ------------------------------------------------------------------------
    Resolve the hero image URL from the images.hero data structure.
    Handles both the new object format (shape-keyed) and the legacy
    flat array format for backward compatibility.

    @param {Object|Array|string} heroData - The hero image data.
    @param {string} shape - The selected shape key (e.g., 'L', 'T').
    @return {string} The resolved hero image URL, or empty string.
    ------------------------------------------------------------------------
    */
    resolveHeroImage(heroData, shape = '') {
        if (!heroData) return '';
        // Legacy: array of URLs
        if (Array.isArray(heroData)) return heroData[0] || '';
        // New: object with shape keys
        if (typeof heroData === 'object') {
            if (shape && heroData[shape]?.length > 0)
                return heroData[shape][0];
            if (heroData.default?.length > 0)
                return heroData.default[0];
            const firstKey = Object.keys(heroData)[0];
            if (firstKey && heroData[firstKey]?.length > 0)
                return heroData[firstKey][0];
        }
        if (typeof heroData === 'string') return heroData;
        return '';
    },
    /*
    ------------------------------------------------------------------------
    Customize the product size nicename output.
    TODO - Derive this nicename directly from the store with data restructuring.

    @param {string} size - The product size code.
    @return {string} The formatted product size name.
    ------------------------------------------------------------------------
    */
    getProductSizeName(size) {
        if (size === 'extralarge') {
            return 'Extra Large';
        }
        return size;
    },
    /*
    ------------------------------------------------------------------------
    Get dimension value from millimeters in specified unit.

    @param {number} value - The value in millimeters.
    @param {string} unit - The target unit ('metric' or 'imperial').
    @return {string} The formatted dimension value with unit label.
    ------------------------------------------------------------------------
    */
    getDimValueFromMm(value, unit) {
        if (unit === 'imperial') {
            return `${this.convertMmToInches(value)}in`;
        } else {
            return `${value}mm`;
        }
    },
    /*
    ------------------------------------------------------------------------
    Convert millimeters to inches.

    @param {number} value - The value in millimeters.
    @return {number} The value in inches, fixed to 1 decimal place.
    ------------------------------------------------------------------------
    */
    convertMmToInches(value) {
        return (value * 0.0393701).toFixed(1);
    },
    /*
    ------------------------------------------------------------------------
    Convert millimeters to feet.

    @param {number} value - The value in millimeters.
    @return {number} The value in feet, fixed to 2 decimal places.
    ------------------------------------------------------------------------
    */
    convertMmToFeet(value) {
        return (value / 304.8).toFixed(2);
    },
    /*
    ------------------------------------------------------------------------
    Convert feet to meters.

    @param {number} value - The value in feet.
    @return {number} The value in meters, fixed to 2 decimal places.
    ------------------------------------------------------------------------
    */
    convertFeetToMeters(value) {
        return (value * 0.3048).toFixed(2);
    },
    /*
    ------------------------------------------------------------------------
    Convert meters to feet.

    @param {number} value - The value in meters.
    @return {number} The value in feet, fixed to 2 decimal places.
    ------------------------------------------------------------------------
    */
    convertMetersToFeet(value) {
        return (value * 3.28084).toFixed(2);
    },
    /*
    ------------------------------------------------------------------------
    Convert feet to millimeters.

    @param {number} value - The value in feet.
    @return {number} The value in millimeters, fixed to 0 decimal places.
    ------------------------------------------------------------------------
    */
    convertFeetToMm(value) {
        return (value * 304.8).toFixed(0);
    },
    /*
    ------------------------------------------------------------------------
    Convert metric sabins to imperial sabins.

    @param {number} metricSabins - The value in metric sabins.
    @return {string} The value in imperial sabins, fixed to 2 decimal places.
    ------------------------------------------------------------------------
    */
    convertMetricToImperialSabins(metricSabins) {
        const conversionFactor = 10.7639;
        return (metricSabins * conversionFactor).toFixed(2);
    },

    /*
    ------------------------------------------------------------------------
    Get furniture image URL.

    @param {string} filenameWithExt - The filename with extension.
    @return {string} The full asset URL.
    ------------------------------------------------------------------------
    */
    getFurnitureImageUrl(filenameWithExt) {
        return this.getAssetUrl(`lx_room_furniture/${filenameWithExt}`);
    },
    /*
    ------------------------------------------------------------------------
    Get product image URL.

    @param {string} filenameWithExt - The filename with extension.
    @return {string} The full asset URL.
    ------------------------------------------------------------------------
    */
    getProductImageUrl(filenameWithExt) {
        return this.getAssetUrl(`lx_product_wires/${filenameWithExt}`);
    },
    /*
    ------------------------------------------------------------------------
    Get asset URL from relative path.

    @param {string} filenameWithExt - The filename with extension.
    @return {string} The full asset URL.
    ------------------------------------------------------------------------
    */
    getAssetUrl(filenameWithExt) {
        const result = new URL(`./assets/${filenameWithExt}`, import.meta.url).href;
        return result;
    },

    /*
    ------------------------------------------------------------------------
    Check if the product has other variants.
    Determines if other variants should be displayed in the UI.

    @param {Object} store - The Pinia store instance.
    @return {boolean} True if the product has other variants.
    ------------------------------------------------------------------------
    */
    productHasVariants(store) {
        const currentProduct = store.currentProduct;
        if (!currentProduct || !currentProduct.data?.sizes?.variants?.all) return false;
        const variantsAll = currentProduct.data.sizes.variants.all;
        if (variantsAll == null || typeof variantsAll !== 'object' || Array.isArray(variantsAll)) return false;
        const hasOtherVariants = Object.keys(variantsAll).length > 0;
        return hasOtherVariants;
    },

    /*
    ------------------------------------------------------------------------
    Trap keyboard focus within a modal.
    Ensures Tab and Shift+Tab cycle through focusable elements without
    escaping the modal.

    @param {string} containerSelector - CSS selector for the modal container.
      Automatically handles both class selectors (e.g., '.lux-modal') and
      ID selectors (e.g., '#my-modal'). Default: '.lux-modal'
    @param {string} type - The type of modal:
      'validation': Selects inputs and tabbable elements within the container
      default: Selects specific product modal elements
    @return {Function} Cleanup function to remove the event listener.
      IMPORTANT: Always call the returned cleanup function when closing
      the modal to prevent memory leaks.

    Example usage:
      const cleanupFocusTrap = Utils.trapModalFocus('.lux-modal', 'validation');
      cleanupFocusTrap();
    ------------------------------------------------------------------------
    */
    trapModalFocus(containerSelector = '.lux-modal', type = 'validation') {
        const modal = document.querySelector(containerSelector);
        let focusableElements;

        if (type === 'validation') {
            focusableElements = modal.querySelectorAll('input, [tabindex]:not([tabindex="-1"]');
        } else {
            /*
            When the full product modal is open we want to include the NEXT
            button in the focus trap (outside of the modal itself).
            */
            focusableElements = document.querySelectorAll('input[name="productsize"], button#next_button, button#modal_close');
        }

        const firstFocusableElement = focusableElements[0];
        const lastFocusableElement = focusableElements[focusableElements.length - 1];

        const handleTabKey = (event) => {
            if (event.key === 'Tab') {
                if (event.shiftKey) {
                    if (document.activeElement === firstFocusableElement) {
                        lastFocusableElement.focus();
                        event.preventDefault();
                    }
                } else {
                    if (document.activeElement === lastFocusableElement) {
                        firstFocusableElement.focus();
                        event.preventDefault();
                    }
                }
            }
        };

        document.addEventListener('keydown', handleTabKey);

        return () => {
            document.removeEventListener('keydown', handleTabKey);
        };
    },
};
